// WP-C1.2 (CAL-1..5, S-34 `/agenda`): the merged agenda — Day/Week toggle, per-calendar
// visibility toggles, colored events, and the optional CAL-3 finance overlay chips. A
// grouped-by-day list rather than a visual month/week grid — S-34's own field list ("Day/Week
// toggle; merged events colored per calendar; per-calendar visibility toggles; optional
// finance overlay chips; 'last refreshed' per calendar with manual refresh") doesn't mandate
// a calendar-grid widget, and a list keeps this WP focused on the data layer it's actually
// named for (event model + agenda), not a bespoke grid renderer.
import type { AgendaEventResponse, AgendaFinanceChip } from '@pfos/shared';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarEventForm,
  type CalendarEventFormEditingEvent,
} from '../components/CalendarEventForm';
import { EmptyState } from '../components/EmptyState';
import { useAgenda } from '../lib/agenda';
import { useCalendars, useRefreshCalendar } from '../lib/calendars';

const KARACHI_OFFSET_MS = 5 * 3_600_000; // Asia/Karachi, fixed UTC+5 (no DST) — same approach as reminders/karachiTime.ts

function startOfKarachiDay(d: Date): Date {
  const shifted = new Date(d.getTime() + KARACHI_OFFSET_MS);
  shifted.setUTCHours(0, 0, 0, 0);
  return new Date(shifted.getTime() - KARACHI_OFFSET_MS);
}

function addDays(d: Date, days: number): Date {
  return new Date(d.getTime() + days * 86_400_000);
}

function karachiDayKey(iso: string): string {
  return new Date(Date.parse(iso) + KARACHI_OFFSET_MS).toISOString().slice(0, 10);
}

function groupByDay(events: AgendaEventResponse[]): Map<string, AgendaEventResponse[]> {
  const map = new Map<string, AgendaEventResponse[]>();
  for (const event of events) {
    const key = karachiDayKey(event.startAt);
    const bucket = map.get(key);
    if (bucket === undefined) map.set(key, [event]);
    else bucket.push(event);
  }
  return map;
}

function dayLabel(dayKey: string): string {
  return new Date(`${dayKey}T12:00:00.000Z`).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function RefreshCalendarButton({ id, label }: { id: string; label: string }): React.JSX.Element {
  const refresh = useRefreshCalendar(id);
  return (
    <button
      type="button"
      aria-label={`Refresh ${label}`}
      className="rounded p-1 text-muted hover:text-ink"
      disabled={refresh.isPending}
      onClick={() => {
        refresh.mutate();
      }}
    >
      <RefreshCw size={12} />
    </button>
  );
}

const FINANCE_CHIP_HREF: Record<AgendaFinanceChip['kind'], string> = {
  payable_due: '/owed',
  recurring_due: '/recurring',
  schedule_due: '/expected',
};

function FinanceChipRow({ chip }: { chip: AgendaFinanceChip }): React.JSX.Element {
  const href = FINANCE_CHIP_HREF[chip.kind];
  return (
    <Link
      to={href}
      className="flex items-center justify-between rounded-[var(--radius-control)] border border-border bg-bg px-3 py-1.5 text-xs hover:bg-surface"
    >
      <span className="text-ink">{chip.label}</span>
      <span className="text-muted">
        {chip.amountLabel ?? '—'} · {new Date(chip.at).toLocaleDateString()}
      </span>
    </Link>
  );
}

export function AgendaPage(): React.JSX.Element {
  const [view, setView] = useState<'day' | 'week'>('week');
  const [anchor, setAnchor] = useState(() => startOfKarachiDay(new Date()));
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  // WP-C2.3 (CW-1/CW-2): one form instance, shared between "New event" (create, editingEvent
  // undefined) and clicking an existing writable event (edit, pre-filled).
  const [formOpen, setFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEventFormEditingEvent | undefined>(
    undefined,
  );

  const from = anchor.toISOString();
  const to = addDays(anchor, view === 'day' ? 1 : 7).toISOString();

  const { data, isLoading } = useAgenda(from, to);
  const { data: calendars } = useCalendars();

  // WP-C2.3: an event's OWN connection tells us if it's read-only — an ICS feed always is
  // (INT-1); an OAuth (google/microsoft) one is treated as writable here (the API exposes no
  // per-connection write-scope field to check more precisely — see CalendarEventForm/Settings
  // Calendars for the same gap; a write attempt against a connection still stuck on the old
  // read-only OAuth grant surfaces as a clear inline error instead of a silently-missing button).
  const readOnlyConnectionIds = useMemo(
    () => new Set((calendars ?? []).filter((c) => c.provider === 'ics').map((c) => c.id)),
    [calendars],
  );

  const events = useMemo(
    () => (data?.events ?? []).filter((e) => !hidden.has(e.calendarConnectionId)),
    [data, hidden],
  );
  const grouped = useMemo(() => groupByDay(events), [events]);
  const dayKeys = useMemo(() => [...grouped.keys()].sort(), [grouped]);

  const openCreate = (): void => {
    setEditingEvent(undefined);
    setFormOpen(true);
  };

  const openEdit = (event: AgendaEventResponse): void => {
    setEditingEvent({
      calendarConnectionId: event.calendarConnectionId,
      calendarLabel: event.calendarLabel,
      uid: event.uid,
      title: event.title,
      startAt: event.startAt,
      endAt: event.endAt,
    });
    setFormOpen(true);
  };

  const toggleCalendar = (id: string): void => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-ink">Agenda</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Previous"
            className="rounded p-1.5 text-muted hover:bg-bg hover:text-ink"
            onClick={() => {
              setAnchor((a) => addDays(a, view === 'day' ? -1 : -7));
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            aria-label="Next"
            className="rounded p-1.5 text-muted hover:bg-bg hover:text-ink"
            onClick={() => {
              setAnchor((a) => addDays(a, view === 'day' ? 1 : 7));
            }}
          >
            <ChevronRight size={16} />
          </button>
          <div className="ml-2 flex rounded-[var(--radius-control)] border border-border">
            <button
              type="button"
              className={`px-3 py-1 text-sm ${view === 'day' ? 'bg-primary/10 font-medium text-primary' : 'text-ink'}`}
              onClick={() => {
                setView('day');
              }}
            >
              Day
            </button>
            <button
              type="button"
              className={`px-3 py-1 text-sm ${view === 'week' ? 'bg-primary/10 font-medium text-primary' : 'text-ink'}`}
              onClick={() => {
                setView('week');
              }}
            >
              Week
            </button>
          </div>
          <button
            type="button"
            className="ml-2 flex items-center gap-1 rounded-[var(--radius-control)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-ink hover:opacity-90"
            onClick={openCreate}
          >
            <Plus size={14} /> New event
          </button>
        </div>
      </div>

      {calendars !== undefined && calendars.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-3">
          {calendars.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-xs text-ink">
              <input
                type="checkbox"
                checked={!hidden.has(c.id)}
                onChange={() => {
                  toggleCalendar(c.id);
                }}
              />
              <span
                aria-hidden="true"
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: c.color ?? 'var(--primary)' }}
              />
              {c.label}
              <RefreshCalendarButton id={c.id} label={c.label} />
              <span className="text-muted">
                {c.lastRefreshError !== null
                  ? 'refresh failed'
                  : c.lastRefreshedAt !== null
                    ? 'refreshed'
                    : 'never refreshed'}
              </span>
            </label>
          ))}
        </div>
      )}

      {data !== undefined && data.financeChips.length > 0 && (
        <div className="mb-4 space-y-1">
          <h2 className="text-xs font-medium uppercase tracking-wide text-muted">
            Money this {view}
          </h2>
          {data.financeChips.map((chip) => (
            <FinanceChipRow key={`${chip.kind}:${chip.entityId}:${chip.at}`} chip={chip} />
          ))}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : dayKeys.length === 0 ? (
        <EmptyState
          icon={CalendarIcon}
          message={
            calendars === undefined || calendars.length === 0
              ? 'No calendars connected yet — add one in Settings → Calendars.'
              : `Nothing on the agenda this ${view}.`
          }
          {...(calendars === undefined || calendars.length === 0
            ? {
                action: {
                  label: 'Connect a calendar',
                  onClick: () => {
                    window.location.assign('/settings/calendars');
                  },
                },
              }
            : {})}
        />
      ) : (
        <div className="space-y-4">
          {dayKeys.map((dayKey) => (
            <section key={dayKey}>
              <h2 className="mb-2 text-sm font-medium text-ink">{dayLabel(dayKey)}</h2>
              <ul className="divide-y divide-border rounded-[var(--radius-card)] border border-border bg-surface">
                {(grouped.get(dayKey) ?? []).map((event) => {
                  // WP-C2.3: a read-only event (ICS, or an OAuth connection not yet upgraded to
                  // write scope — see readOnlyConnectionIds above) gets NO edit/delete
                  // affordance at all, not merely a server-side block.
                  const readOnly = readOnlyConnectionIds.has(event.calendarConnectionId);
                  const row = (
                    <>
                      <span
                        aria-hidden="true"
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: event.calendarColor ?? 'var(--primary)' }}
                      />
                      <span className="w-16 shrink-0 text-xs text-muted">
                        {event.allDay ? 'All day' : timeLabel(event.startAt)}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-ink">{event.title}</span>
                      <span className="shrink-0 text-xs text-muted">{event.calendarLabel}</span>
                    </>
                  );
                  return readOnly ? (
                    <li key={event.id} className="flex items-center gap-3 px-4 py-2 text-sm">
                      {row}
                    </li>
                  ) : (
                    <li key={event.id}>
                      <button
                        type="button"
                        className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-bg"
                        onClick={() => {
                          openEdit(event);
                        }}
                      >
                        {row}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}

      <CalendarEventForm
        open={formOpen}
        onOpenChange={setFormOpen}
        {...(editingEvent !== undefined ? { editing: editingEvent } : {})}
      />
    </div>
  );
}
